import React, { useState, useEffect } from 'react';
import adminAPI from '../../lib/api';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-[12px] bg-[#121212] border border-white/10 p-3 shadow-lg">
      <p className="text-xs text-white/50 uppercase tracking-wider mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-sm font-medium" style={{ color: p.color }}>{p.name}: {p.value}</p>
      ))}
    </div>
  );
};

export default function Analytics() {
  const [daily, setDaily] = useState([]);
  const [retention, setRetention] = useState([]);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [days]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    setLoading(true);
    try {
      const [dl, ret] = await Promise.all([
        adminAPI.getAnalyticsDaily(days),
        adminAPI.getAnalyticsRetention()
      ]);
      setDaily(Array.isArray(dl) ? dl : []);
      setRetention(ret?.cohorts || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-[#CCFF00] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 sm:mb-8">
        <div>
          <h1 className="font-['Barlow_Condensed'] font-bold text-2xl sm:text-3xl md:text-4xl text-white">Analyse</h1>
          <p className="text-xs sm:text-sm text-white/40 mt-1">Daglige metrikker og brukerretensjon</p>
        </div>
        <select
          data-testid="admin-analytics-date-range"
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="h-10 px-4 rounded-[14px] bg-[#121212] border border-white/10 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#CCFF00]/35"
        >
          <option value={7}>7 dager</option>
          <option value={14}>14 dager</option>
          <option value={30}>30 dager</option>
          <option value={60}>60 dager</option>
        </select>
      </div>

      {/* Daily charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6 sm:mb-8">
        <div data-testid="recharts-daily-line-chart" className="rounded-[16px] bg-[rgba(255,255,255,0.03)] border border-white/5 p-5">
          <p className="uppercase tracking-[0.18em] text-[11px] font-medium text-white/40 mb-4">AKTIVE BRUKERE PER DAG</p>
          {daily.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={daily}>
                <defs>
                  <linearGradient id="voltGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#CCFF00" stopOpacity={0.25} />
                    <stop offset="100%" stopColor="#CCFF00" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                <XAxis dataKey="date" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} stroke="rgba(255,255,255,0.18)" />
                <YAxis tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} stroke="rgba(255,255,255,0.18)" />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="active_users" stroke="#CCFF00" strokeWidth={2} fill="url(#voltGrad)" name="Aktive" />
              </AreaChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-white/30 text-center py-16">Ingen data for perioden</p>}
        </div>

        <div className="rounded-[16px] bg-[rgba(255,255,255,0.03)] border border-white/5 p-5">
          <p className="uppercase tracking-[0.18em] text-[11px] font-medium text-white/40 mb-4">REGISTRERINGER</p>
          {daily.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={daily}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                <XAxis dataKey="date" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} stroke="rgba(255,255,255,0.18)" />
                <YAxis tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} stroke="rgba(255,255,255,0.18)" />
                <Tooltip content={<CustomTooltip />} />
                <Line type="monotone" dataKey="signups" stroke="#5AC8FA" strokeWidth={2} dot={false} name="Registreringer" />
              </LineChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-white/30 text-center py-16">Ingen data for perioden</p>}
        </div>
      </div>

      {/* Workouts chart */}
      <div className="rounded-[16px] bg-[rgba(255,255,255,0.03)] border border-white/5 p-5 mb-8">
        <p className="uppercase tracking-[0.18em] text-[11px] font-medium text-white/40 mb-4">ØKTER GJENNOMFØRT vs PLANLAGT</p>
        {daily.length > 0 ? (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={daily}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
              <XAxis dataKey="date" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} stroke="rgba(255,255,255,0.18)" />
              <YAxis tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} stroke="rgba(255,255,255,0.18)" />
              <Tooltip content={<CustomTooltip />} />
              <Line type="monotone" dataKey="workouts_total" stroke="rgba(255,255,255,0.25)" strokeWidth={1.5} strokeDasharray="4 4" dot={false} name="Planlagt" />
              <Line type="monotone" dataKey="workouts_completed" stroke="#34C759" strokeWidth={2} dot={false} name="Gjennomført" />
            </LineChart>
          </ResponsiveContainer>
        ) : <p className="text-sm text-white/30 text-center py-16">Ingen data for perioden</p>}
      </div>

      {/* Retention */}
      <div data-testid="admin-analytics-retention-cohort-table" className="rounded-[16px] bg-[rgba(255,255,255,0.03)] border border-white/5 p-5">
        <p className="uppercase tracking-[0.18em] text-[11px] font-medium text-white/40 mb-4">RETENSJON (KOHORTER)</p>
        {retention.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/5">
                  <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider text-white/30">Kohort</th>
                  <th className="text-right px-4 py-2 text-[10px] uppercase tracking-wider text-white/30">Størrelse</th>
                  <th className="text-right px-4 py-2 text-[10px] uppercase tracking-wider text-white/30">Aktive</th>
                  <th className="text-right px-4 py-2 text-[10px] uppercase tracking-wider text-white/30">Retensjon</th>
                </tr>
              </thead>
              <tbody>
                {retention.map((c, i) => (
                  <tr key={i} className="border-b border-white/[0.03]">
                    <td className="px-4 py-2.5 text-sm text-white/70">{c.week}</td>
                    <td className="px-4 py-2.5 text-sm text-white/50 text-right">{c.cohort_size}</td>
                    <td className="px-4 py-2.5 text-sm text-white/50 text-right">{c.still_active}</td>
                    <td className="px-4 py-2.5 text-right">
                      <span className={`inline-block px-2.5 py-1 rounded-md text-xs font-medium ${
                        c.retention > 50 ? 'bg-[#34C759]/15 text-[#34C759]' :
                        c.retention > 20 ? 'bg-[#FF9F0A]/15 text-[#FF9F0A]' :
                        c.cohort_size === 0 ? 'bg-white/5 text-white/20' :
                        'bg-[#FF3B30]/15 text-[#FF3B30]'
                      }`} style={{ minWidth: 50 }}>
                        {c.cohort_size > 0 ? `${Math.round(c.retention)}%` : '—'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p className="text-sm text-white/30 text-center py-16">Ingen kohorter ennå</p>}
      </div>
    </div>
  );
}
